import type { FileUploadRestrictions } from '../types';
import type { DragEvent } from 'react';

import { useToggle } from '@uidotdev/usehooks';
import { useDropzone, useFileInput } from '@uppy/react';
import { cn } from 'cn';
import { useTranslation } from 'react-i18next';
import { Divider, Hint } from '~/atoms';
import { Button } from '~/molecules';

import { getRestrictionsHint } from '../utils/getRestrictionsHint';

import { FolderIllustration } from './FolderIllustration';

type P = {
  readonly isDisabled?: boolean;
  readonly buttonText?: string;
  readonly restrictions?: FileUploadRestrictions;
  readonly showRestrictionsHint: boolean;
} & ComponentStyling;

export function Dropzone({
  buttonText,
  className,
  isDisabled = false,
  restrictions,
  showRestrictionsHint,
  style,
}: P) {
  const { t } = useTranslation('ui');

  const allowsMultiple =
    restrictions?.maxNumberOfFiles == null || restrictions.maxNumberOfFiles > 1;
  const fileCount = allowsMultiple ? 2 : 1;
  const resolvedButtonText =
    buttonText ?? t('uploader.selectFile', { count: fileCount });
  const [isDragging, toggleDragging] = useToggle(false);

  const { getRootProps, getInputProps: getDragAndDropInputProps } = useDropzone(
    {
      noClick: true,
      onDragLeave: () => {
        toggleDragging(false);
      },
      onDragOver: () => {
        toggleDragging(true);
      },
    },
  );
  const { getButtonProps, getInputProps: getFileInputProps } = useFileInput();

  const dragAndDropInputProps = getDragAndDropInputProps();
  const fileInputProps = getFileInputProps();
  // Without Uppy's handlers a drop would make the browser open the file.
  const rootProps = isDisabled
    ? {
        onDragOver: (event: DragEvent<HTMLElement>) => {
          event.preventDefault();
        },
        onDrop: (event: DragEvent<HTMLElement>) => {
          event.preventDefault();
        },
      }
    : getRootProps();

  const restrictionsHint = getRestrictionsHint(restrictions, {
    t,
  });
  return (
    <div className={cn('uploader-dropzone relative', className)} style={style}>
      <input
        {...dragAndDropInputProps}
        className="hidden"
        disabled={isDisabled}
      />
      <input {...fileInputProps} className="hidden" disabled={isDisabled} />

      <div
        {...rootProps}
        className={cn(
          'uploader-dropzone-area group flex size-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center text-sm transition-all duration-600',
          isDisabled
            ? 'cursor-not-allowed border-tiga-action-default opacity-50'
            : 'hover:border-tiga-action-lighter hover:bg-tiga-action-lighter/13',
          !isDisabled && isDragging
            ? 'border-tiga-action-lighter bg-tiga-action-lighter/13'
            : 'border-tiga-action-default',
        )}
      >
        <div className="mt-2 flex flex-col items-center justify-center gap-1 select-none">
          <FolderIllustration className="size-12" />
          {t('uploader.dragHint', { count: fileCount })}
        </div>
        <Divider className="mt-1 mb-2 w-full max-w-80 select-none">
          {t(($) => $.uploader.or)}
        </Divider>
        <Button
          buttonStyle="secondary"
          text={resolvedButtonText}
          className=""
          disabled={isDisabled}
          onClick={getButtonProps().onClick}
        />
        {showRestrictionsHint && restrictionsHint && (
          <Hint className="mt-auto block text-xs">{restrictionsHint}</Hint>
        )}
      </div>
    </div>
  );
}
